import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import PropTypes from 'prop-types';
import React from 'react';
import { withStyles } from 'tss-react/mui';

import IdGenerator from './IdGenerator';
import QListDetails from './QListDetails';
import QListHeader from './QListHeader';
import QListToolbar from './QListToolbar';

const ROW_HEIGHT = 38;

const styles = ({ palette, components }) => ({
  root: {
    border: `1px solid ${palette.divider}`,
  },
  table: {
    paddingLeft: 24,
    paddingRight: 24,
    display: 'flex',
    flexFlow: 'column',
    height: 'inherit',
  },
  tableWrapper: {
    overflowX: 'auto',
    height: '100%',
  },
  rowContainer: {
    display: 'flex',
    background: palette.background.default,
    cursor: 'pointer',
    height: ROW_HEIGHT,
    borderBottom: 'solid',
    borderBottomColor: palette.border.main,
    borderBottomWidth: 1,
    '&:focus': {
      outline: 'none',
    },
    overflow: 'hidden',
  },
  rowItem: {
    margin: 'auto',
    flex: 1,
    fontSize: components.register.fontSize.small,
  },
  checkRow: {
    minWidth: 45,
    flex: 0,
  },
  footer: {
    height: 30,
    flex: '0 1 30px',
  },
  rowsSection: {
    flex: '1 1 auto',
    paddingTop: 5,
    paddingBottom: 5,
  },
  header: {
    flex: '0 1 auto',
    minHeight: 30,
  },
  checkButton: {
    height: ROW_HEIGHT,
  },
});

function sortValue(val) {
  if (typeof val === 'string') {
    return val.toLowerCase();
  }
  return val;
}

class QList extends React.Component {
  constructor(props, context) {
    super(props, context);

    this.state = {
      order: 'desc',
      orderBy: props.defaultSort,
      selected: [],
      open: [],
      showChildren: [],
      data: this.doSort(props.data, props.defaultSort, 'desc'),
    };
  }

  componentDidMount() {
    window.addEventListener('resize', this.sizeChanged);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.data !== this.props.data) {
      this.setState({ data: this.doSort(nextProps.data, nextProps.defaultSort, this.state.order) });
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.sizeChanged);
  }

  sizeChanged = () => {
    this.forceUpdate();
  };

  doSort = (data, orderBy, order) => {
    const newData = [].concat(data);
    if (order === 'asc') {
      newData.sort((a, b) => (sortValue(b[orderBy]) < sortValue(a[orderBy]) ? -1 : 1));
    } else {
      newData.sort((a, b) => (sortValue(a[orderBy]) < sortValue(b[orderBy]) ? -1 : 1));
    }
    return newData;
  };

  handleRequestSort = (event, property) => {
    const orderBy = property;

    let order = 'desc';
    if (this.state.orderBy === property && this.state.order === 'desc') {
      order = 'asc';
    }

    this.setState({ data: this.doSort(this.state.data, orderBy, order), order, orderBy });
  };

  handleRequestCreate = () => {
    // add a new default item, open it up for editing.  Do not add if there is already a "new" item, instead
    // ensure that one is open

    const newIndex = this.state.data.findIndex((x) => x.id === 0);

    const overrides = this.props.makeNewObject ? this.props.makeNewObject() : {};

    if (newIndex < 0) {
      let newItem = {};
      const newData = [].concat(this.state.data);

      this.props.dataDictionary.forEach((field) => {
        newItem[field.id] = field.type === 'number' ? 0 : '';
      });

      newItem.id = 0;
      newItem = { ...newItem, ...overrides };

      newData.unshift(newItem);
      this.setState({ data: newData });
    }
  };

  handleSelectAllClick = (event, checked) => {
    if (checked) {
      this.setState({ selected: this.state.data.map((n) => n.id) });
      return;
    }
    this.setState({ selected: [] });
  };

  listAction = (list, id) => {
    const selectedIndex = list.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(list, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(list.slice(1));
    } else if (selectedIndex === list.length - 1) {
      newSelected = newSelected.concat(list.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(list.slice(0, selectedIndex), list.slice(selectedIndex + 1));
    }
    return newSelected;
  };

  handleClick = (event, id) => {
    event.stopPropagation();
    this.setState({ selected: this.listAction(this.state.selected, id) });
  };

  handleRowClick = (event, id) => {
    if (event) {
      event.stopPropagation();
    }
    this.setState({ open: this.listAction(this.state.open, id) });

    // if in the 'new' row, delete it
    if (id === 0) {
      const newIndex = this.state.data.findIndex((x) => x.id === 0);
      this.state.data.splice(newIndex, 1);
    }
  };

  handleSave = (d) => {
    let data = d;

    // Transform the object if requested
    if (this.props.makeRequestObject) {
      data = this.props.makeRequestObject(d);
    }

    if (this.props.onSave) {
      this.props.onSave(data);
    }

    // delete the item from the 'open' list
    const newIndex = this.state.data.findIndex((x) => x.id === data.id);

    // If this was the "new" item, delete it completely
    if (data.id === 0 && newIndex >= 0) {
      const newData = [].concat(this.state.data);
      newData.splice(newIndex, 1);
      this.setState({ data: newData });
    }
    const openIndex = this.state.open.indexOf(data.id);

    if (openIndex >= 0) {
      // create a new open items array without the item given
      const newOpen = [].concat(this.state.open);
      newOpen.splice(openIndex, 1);
      this.setState({ open: newOpen });
    }
  };

  showChildren = (e, id) => {
    let newArray = this.state.showChildren;
    if (this.showChildrenOfParent(id)) {
      newArray = this.state.showChildren.filter((x) => x !== id);
    } else {
      newArray.push(id);
    }

    e.stopPropagation();

    this.setState({ showChildren: newArray });
  };

  isSelected = (id) => this.state.selected.indexOf(id) !== -1;

  isOpen = (id) => this.state.open.indexOf(id) !== -1 || id === 0;

  showChildrenOfParent = (id) => this.state.showChildren.indexOf(id) !== -1;

  renderDataColumns = ({ disableDelete, level, childData, dataDictionary, item, classes, baseId }) => {
    const row = [];
    let first = true;
    const isSelected = this.isSelected(item.id);

    const isOpen = this.showChildrenOfParent(item.id);

    if (!disableDelete) {
      row.push(
        <div key={`${baseId}-row-checkbox-${item.id}`} className={classes.checkRow}>
          <Checkbox
            color='primary'
            inputProps={{
              'aria-label': 'Select Item',
              id: `${baseId}-row-checkbox`,
            }}
            style={{ fontSize: '21px', marginLeft: level * 12 }}
            className={classes.checkButton}
            checked={isSelected}
            onClick={(event) => this.handleClick(event, item.id)}
          />
        </div>,
      );
    }
    dataDictionary
      .filter((x) => !x.detailsOnly)
      .forEach((col) => {
        const leftMargin = first ? 12 * level : 0;

        row.push(
          <div key={`qlist_cell:${col.id}-${item[col.id]}`} style={{ flex: col.flex }} className={classes.rowItem}>
            <div
              style={{
                fontSize: col.font === 'small' ? '13px' : null,
                textAlign: 'left',
                paddingLeft: leftMargin, // + (first && 25),
                display: 'inline-block',
              }}
              id={`${baseId}-row-data-${col.id}`}
            >
              {col.type === 'custom' ? col.stringToCustom(item[col.id]) : item[col.id] || ''}
            </div>
          </div>,
        );
        first = false;
      });
    row.push(
      childData ? (
        <IconButton
          id={`${baseId}-toggle`}
          aria-label='Expand or Collapse'
          style={{
            minWidth: 25,
            flex: 0,
            height: 25,
            margin: 'auto',
            padding: 0,
            marginRight: 12,
          }}
          key={`qlist_cell_tree:${item.id}`}
          onClick={(e) => this.showChildren(e, item.id)}
          size='large'
        >
          <ArrowDropDownRoundedIcon style={{ transform: isOpen ? '' : 'scaleY(-1)', transition: 'all 0.6s ease' }} />
        </IconButton>
      ) : (
        <div
          style={{
            width: 25,
            height: 25,
            display: 'inline-block',
            margin: 'auto',
            padding: 0,
            marginRight: 12,
          }}
          key={`qlist_cell_tree:${item.id}`}
        />
      ),
    );
    return row;
  };

  renderListItem = ({ level, item, classes, dataDictionary, disableDelete, detailsComponent, idGenerator }) => {
    const isSelected = this.isSelected(item.id);
    const isOpen = this.isOpen(item.id);

    const DetailsComponent = detailsComponent || QListDetails;

    let childData = null;
    if (item.children) {
      childData = item.children.map((kid) =>
        this.renderListItem({
          level: level + 1,
          item: kid,
          classes,
          dataDictionary,
          disableDelete,
          detailsComponent,
          idGenerator: new IdGenerator(idGenerator.getCurrent()),
        }),
      );
    }
    const id = idGenerator.getNext();
    let parentRow = [
      <React.Fragment key={`qlist_table:${item.name}${item.id}`}>
        <Paper style={{ marginBottom: isOpen ? 15 : 0 }} elevation={0}>
          <Paper
            role='checkbox'
            aria-checked={isSelected}
            tabIndex={-1}
            key={item.id}
            selected={isSelected}
            elevation={0}
            className={classes.rowContainer}
            onClick={(event) => this.handleRowClick(event, item.id)}
            style={this.props.rowHeight ? { height: this.props.rowHeight } : null}
          >
            {this.renderDataColumns({ disableDelete, level, childData, dataDictionary, item, classes, baseId: id })}
          </Paper>
          {isOpen && (
            <DetailsComponent
              className={classes.details}
              dataDictionary={dataDictionary}
              data={item}
              onEscape={() => this.handleRowClick(null, item.id)}
              onSave={this.handleSave}
            />
          )}
        </Paper>
      </React.Fragment>,
    ];

    if (this.showChildrenOfParent(item.id) && childData) {
      parentRow = parentRow.concat(childData);
    }
    return parentRow;
  };

  render() {
    const { classes, dataDictionary, detailsComponent, onDelete, disableDelete, onValidateRequest, listItemType } =
      this.props;
    const { data, order, orderBy, selected } = this.state;
    const idGenerator = new IdGenerator(listItemType);
    return (
      <div className={classes.root}>
        <QListToolbar
          listName={listItemType}
          numSelected={selected.length}
          onRequestCreate={this.handleRequestCreate}
          onValidateRequest={onValidateRequest}
          onDelete={() => {
            if (onDelete) {
              onDelete(this.state.selected);
              this.setState({ selected: [] });
            }
          }}
        />
        <div className={classes.tableWrapper}>
          <div className={classes.table}>
            <div className={classes.header}>
              <QListHeader
                numSelected={selected.length}
                order={order}
                orderBy={orderBy}
                onSelectAllClick={this.handleSelectAllClick}
                onRequestSort={this.handleRequestSort}
                rowCount={data.length}
                columnData={dataDictionary.filter((x) => !x.detailsOnly)}
                disableDelete={disableDelete}
              />
            </div>

            <div className={classes.rowsSection}>
              {data.map((item) =>
                this.renderListItem({
                  level: 0,
                  item,
                  classes,
                  dataDictionary,
                  disableDelete,
                  detailsComponent,
                  idGenerator,
                }),
              )}
            </div>
          </div>
        </div>
        <div className={classes.footer}></div>
      </div>
    );
  }
}

QList.propTypes = {
  classes: PropTypes.object.isRequired,
  data: PropTypes.array.isRequired,
  dataDictionary: PropTypes.array.isRequired,
  defaultSort: PropTypes.string,
  detailsComponent: PropTypes.any,
  onSave: PropTypes.func,
  onDelete: PropTypes.func,
  makeNewObject: PropTypes.func,
  makeRequestObject: PropTypes.func,
  disableDelete: PropTypes.bool,
  onValidateRequest: PropTypes.func,
  rowHeight: PropTypes.number,
  listItemType: PropTypes.string,
};

export default withStyles(QList, styles);
